import { useEffect, useRef, useState } from 'react';

/**
 * Попал ли элемент в поле зрения.
 *
 * ## Зачем
 *
 * Микровзаимодействие на скролле: инфографика карточек фактов оживает, когда
 * до неё домотали, а не пока она за нижней кромкой экрана (#1330). Анимация,
 * отыгравшая до того, как на неё посмотрели, — это просто её отсутствие.
 *
 * ## Почему `once` по умолчанию
 *
 * Повторный проигрыш при каждом заходе в кадр превращает страницу в
 * мигающую: при обычной прокрутке блок пересекает границу по нескольку раз.
 * Показали — и хватит.
 *
 * ## Где `IntersectionObserver` может отсутствовать
 *
 * В jsdom (юнит-тесты) его нет вовсе. Там и в любом браузере без API хук
 * сразу отвечает «в кадре»: отсутствие наблюдателя не повод прятать
 * содержимое — оно должно быть видно даже без анимации.
 *
 * @param {object} [options]
 * @param {boolean} [options.once] — перестать следить после первого попадания.
 * @param {number} [options.threshold] — какая доля элемента должна быть видна.
 * @returns {[React.RefObject, boolean]} ref для элемента и признак «в кадре».
 */
export function useInView({ once = true, threshold = 0.25 } = {}) {
  const ref = useRef(null);
  const supported = typeof IntersectionObserver !== 'undefined';
  const [inView, setInView] = useState(!supported);

  useEffect(() => {
    if (!supported) return undefined;
    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [once, threshold, supported]);

  return [ref, inView];
}

export default useInView;
